import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { api, apiErrorMessage } from "@/lib/api";
import { X, ChevronDown, Copy, Check } from "lucide-react";

const SOCIAL_NICHES = [
  "Gaming", "Meme", "Brainrot", "Music Edits", "Fitness",
  "Sports", "Tech", "Lifestyle", "AI Content", "Clipping",
  "Movies", "Podcast", "Other",
];

const PLATFORMS = [
  {
    id: "Instagram",
    label: "Instagram",
    disabled: false,
    example: "https://www.instagram.com/clippingbase.official",
    icon: (
      <span className="grid h-8 w-8 place-items-center rounded-[9px]" style={{ background: "linear-gradient(135deg,#f9ce34,#ee2a7b 45%,#6228d7)" }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="1.2" fill="white" />
        </svg>
      </span>
    ),
  },
  {
    id: "TikTok",
    label: "TikTok",
    disabled: false,
    example: "https://www.tiktok.com/@yourhandle",
    icon: (
      <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-black">
        <svg width="15" height="17" viewBox="0 0 24 24" fill="white">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.72a8.19 8.19 0 004.76 1.52V6.79a4.83 4.83 0 01-1-.1z" />
        </svg>
      </span>
    ),
  },
  {
    id: "X",
    label: "X/Twitter",
    disabled: true,
    example: "Coming soon",
    icon: (
      <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-black">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="white">
          <path d="M18.244 2H21.5l-7.5 8.586L23 22h-6.844l-5.4-6.98L4.5 22H1.244l8.048-9.207L1 2h6.914l4.914 6.523L18.244 2zm-2.4 18h1.9L7.244 4h-2L15.844 20z" />
        </svg>
      </span>
    ),
  },
  {
    id: "YouTube",
    label: "YouTube",
    disabled: false,
    example: "https://www.youtube.com/@yourchannel",
    icon: (
      <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-[#FF0000]">
        <svg width="16" height="14" viewBox="0 0 24 24" fill="white">
          <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 00.5 6.2a31.4 31.4 0 000 11.6 3 3 0 002.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 002.1-2.1 31.4 31.4 0 000-11.6zM9.75 15.5v-7l6.25 3.5-6.25 3.5z" />
        </svg>
      </span>
    ),
  },
];

// Stepper dots (matches mockup: filled dot for current, thin dots for others)
function StepDots({ step, total = 4 }) {
  return (
    <div className="mt-5 flex items-center justify-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          data-testid={`socials-wizard-dot-${i}`}
          className={
            i === step
              ? "h-1.5 w-6 rounded-full bg-white"
              : "h-1.5 w-1.5 rounded-full bg-white/30"
          }
        />
      ))}
    </div>
  );
}

function StepHeader({ title, subtitle, onClose }) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3">
      <div>
        <h2 className="text-[19px] font-bold tracking-tight text-white">{title}</h2>
        {subtitle && (
          <p className="mt-0.5 text-[12.5px] text-zinc-400">{subtitle}</p>
        )}
      </div>
      <button
        type="button"
        data-testid="socials-wizard-close"
        onClick={onClose}
        aria-label="Close"
        className="grid h-7 w-7 place-items-center rounded-md text-zinc-500 hover:bg-white/[0.06] hover:text-zinc-200"
      >
        <X size={16} />
      </button>
    </div>
  );
}

function NavBar({ onBack, onNext, nextLabel = "Continue", nextDisabled, nextLoading, showBack = true, closeLabel = "Close", isClose = false }) {
  return (
    <div className="mt-6 grid grid-cols-2 gap-3">
      {showBack ? (
        <Button
          data-testid="socials-wizard-back"
          type="button"
          variant="ghost"
          onClick={onBack}
          className="h-11 rounded-xl border border-white/[0.08] bg-white/[0.02] text-[14px] font-semibold text-white hover:bg-white/[0.05]"
        >
          Back
        </Button>
      ) : (
        <Button
          data-testid="socials-wizard-cancel"
          type="button"
          variant="ghost"
          onClick={onBack}
          className="h-11 rounded-xl border border-white/[0.08] bg-white/[0.02] text-[14px] font-semibold text-white hover:bg-white/[0.05]"
        >
          {closeLabel}
        </Button>
      )}
      <Button
        data-testid="socials-wizard-next"
        type="button"
        onClick={onNext}
        disabled={nextDisabled || nextLoading}
        className="h-11 rounded-xl bg-[#c8ff00] text-[14px] font-bold text-black hover:bg-[#b8ef00] active:scale-[0.97] disabled:opacity-50"
      >
        {nextLoading ? "…" : nextLabel}
      </Button>
    </div>
  );
}

export function SocialWizardDialog({ open, onOpenChange, onCompleted }) {
  const [step, setStep] = useState(0); // 0=platform, "method"=IG chooser, 1=handle, 2=theme, 3=verify
  const [platform, setPlatform] = useState(null);
  const [method, setMethod] = useState(null); // null | "oauth" | "manual"
  const [handle, setHandle] = useState("");
  const [niches, setNiches] = useState([]);
  const [themeOpen, setThemeOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [socialId, setSocialId] = useState(null);
  const [code, setCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [oauthStarting, setOauthStarting] = useState(false);
  const [error, setError] = useState(null);

  // Reset on close
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep(0); setPlatform(null); setMethod(null); setHandle(""); setNiches([]);
        setSocialId(null); setCode(null); setCopied(false); setError(null);
        setThemeOpen(false); setHelpOpen(false); setOauthStarting(false);
      }, 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  const platformExample = useMemo(() => PLATFORMS.find((p) => p.id === platform)?.example, [platform]);
  const chosenPlatform = useMemo(() => PLATFORMS.find((p) => p.id === platform), [platform]);

  async function createSocial() {
    setSaving(true);
    setError(null);
    try {
      const res = await api.post("/socials", {
        platform,
        handle: handle.trim(),
        niches,
      });
      const socials = res.data?.socials || [];
      // Backend $pushes the newly-created social to the end of the array,
      // so socials[0] is the OLDEST. Find the one we just created by
      // matching platform + handle (with/without leading @).
      const cleanHandle = handle.trim().replace(/^@/, "");
      const created =
        socials.find(
          (s) =>
            s.platform === platform &&
            (s.handle === handle.trim() ||
              s.handle === cleanHandle ||
              s.handle === `@${cleanHandle}` ||
              String(s.handle || "").replace(/^@/, "").toLowerCase() ===
                cleanHandle.toLowerCase())
        ) || socials[socials.length - 1];
      if (created?.id) setSocialId(created.id);
      if (created?.verification?.code) setCode(created.verification.code);
      setStep(3);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function runVerify() {
    if (!socialId) return;
    setVerifying(true);
    setError(null);
    try {
      const res = await api.post("/socials/verify/check", { social_id: socialId });
      if (res.data?.verified) {
        toast({ title: "Social verified", description: "Your account is now verified via bio code." });
        onCompleted?.();
        onOpenChange?.(false);
      } else {
        setError("Code not found in bio yet. Add it and try again.");
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setVerifying(false);
    }
  }

  function copyCode() {
    if (!code) return;
    try {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard may be unavailable */ }
  }

  async function startInstagramOAuth() {
    setOauthStarting(true);
    setError(null);
    try {
      const res = await api.get("/socials/instagram/connect");
      const url = res.data?.authorize_url;
      if (!url) throw new Error("No authorize URL returned");
      // Full-page redirect to Meta authorize.
      window.location.assign(url);
    } catch (e) {
      setError(apiErrorMessage(e));
      setOauthStarting(false);
    }
  }

  function advanceFromPlatform() {
    // Instagram → show method chooser first
    if (platform === "Instagram") {
      setStep("method");
      return;
    }
    setMethod("manual");
    setStep(1);
  }

  // Step gates
  const canNextFromPlatform = !!platform;
  const canNextFromHandle = handle.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="socials-wizard-dialog"
        className="max-w-md rounded-3xl border-white/[0.08] bg-[#0f0f0f] p-6 text-white shadow-[0_30px_80px_rgba(0,0,0,0.6)] [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <VisuallyHidden>
          <DialogTitle>Add social account</DialogTitle>
          <DialogDescription>Step-by-step wizard to connect and verify a new social platform account.</DialogDescription>
        </VisuallyHidden>
        {/* Step 1: Platform */}
        {step === 0 && (
          <div data-testid="socials-wizard-step-platform">
            <StepHeader
              title="Platform"
              subtitle="Select a platform you wish to connect."
              onClose={() => onOpenChange?.(false)}
            />
            <div className="grid grid-cols-2 gap-3">
              {PLATFORMS.map((p) => {
                const selected = platform === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`socials-wizard-platform-${p.id.toLowerCase()}`}
                    disabled={p.disabled}
                    onClick={() => setPlatform(p.id)}
                    className={[
                      "relative flex items-center gap-3 rounded-2xl border px-3 py-3 text-left transition-all active:scale-[0.98]",
                      p.disabled
                        ? "border-white/[0.05] bg-white/[0.02] opacity-60 cursor-not-allowed"
                        : selected
                        ? "border-[#c8ff00] bg-[#c8ff00]/[0.06]"
                        : "border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.05]",
                    ].join(" ")}
                  >
                    {p.icon}
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-semibold text-white">{p.label}</div>
                      {p.disabled && (
                        <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-400/90">
                          Coming soon
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <StepDots step={0} />
            <NavBar
              showBack={false}
              closeLabel="Close"
              onBack={() => onOpenChange?.(false)}
              onNext={advanceFromPlatform}
              nextDisabled={!canNextFromPlatform}
            />
          </div>
        )}

        {/* Step 1.5: Method chooser (Instagram only) */}
        {step === "method" && (
          <div data-testid="socials-wizard-step-method">
            <StepHeader
              title="Connect Instagram"
              subtitle="Pick how you want to verify this account."
              onClose={() => onOpenChange?.(false)}
            />

            <div className="space-y-3">
              {/* Recommended: OAuth */}
              <button
                type="button"
                data-testid="socials-wizard-method-oauth"
                onClick={startInstagramOAuth}
                disabled={oauthStarting}
                className="group relative flex w-full flex-col gap-1 rounded-2xl border border-[#c8ff00]/40 bg-[#c8ff00]/[0.06] p-4 text-left transition-all hover:bg-[#c8ff00]/[0.10] active:scale-[0.98] disabled:opacity-70"
              >
                <div className="absolute right-3 top-3 rounded-full bg-[#c8ff00] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black">
                  Recommended
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className="grid h-10 w-10 place-items-center rounded-xl"
                    style={{ background: "linear-gradient(135deg,#f9ce34,#ee2a7b 45%,#6228d7)" }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="5" />
                      <circle cx="12" cy="12" r="4" />
                      <circle cx="17.5" cy="6.5" r="1.2" fill="white" />
                    </svg>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[14.5px] font-bold text-white">
                      {oauthStarting ? "Redirecting to Instagram…" : "Connect Instagram"}
                    </div>
                    <div className="mt-0.5 text-[12px] text-zinc-400">
                      One-click. Auto-verified. Enables auto view/like tracking.
                    </div>
                  </div>
                </div>
              </button>

              {/* Manual bio-code flow */}
              <button
                type="button"
                data-testid="socials-wizard-method-manual"
                onClick={() => { setMethod("manual"); setStep(1); }}
                className="group flex w-full items-center gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 text-left transition-all hover:bg-white/[0.05] active:scale-[0.98]"
              >
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-white/[0.06] text-white/80 font-mono text-[13px] font-bold">
                  #
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[14.5px] font-bold text-white">Verify with bio code</div>
                  <div className="mt-0.5 text-[12px] text-zinc-400">
                    Paste a code into your bio. Works for any Instagram account.
                  </div>
                </div>
              </button>

              <div className="rounded-lg border border-white/[0.06] bg-black/40 px-3 py-2 text-[11px] text-zinc-500">
                &quot;Connect Instagram&quot; requires a{" "}
                <span className="font-semibold text-zinc-300">Business or Creator</span>{" "}
                account (free upgrade in Instagram Settings → Account).
              </div>

              {error && (
                <div className="rounded-lg border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
                  {error}
                </div>
              )}
            </div>

            <StepDots step={0} />
            <NavBar
              onBack={() => setStep(0)}
              onNext={() => { setMethod("manual"); setStep(1); }}
              nextLabel="Skip &amp; use bio code"
            />
          </div>
        )}

        {/* Step 2: Handle */}
        {step === 1 && (
          <div data-testid="socials-wizard-step-handle">
            <StepHeader
              title="Your handle"
              onClose={() => onOpenChange?.(false)}
            />
            <div className="space-y-3">
              <div className="text-[13px] font-medium text-zinc-300">@Handle or profile link</div>
              <div className="flex items-center gap-2 rounded-xl border border-white/[0.08] bg-black/40 px-3 py-2.5">
                <span className="shrink-0">{chosenPlatform?.icon}</span>
                <Input
                  data-testid="socials-wizard-handle-input"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder={`@handle or ${platformExample || "https://…"}`}
                  className="border-0 bg-transparent px-0 text-[13.5px] text-white placeholder:text-zinc-500 focus-visible:ring-0"
                />
              </div>
              <div className="pt-1">
                <div className="text-[12.5px] font-semibold text-zinc-300">Examples:</div>
                <div className="mt-1 text-[12px] text-zinc-500">
                  <span className="text-zinc-400">{platformExample}</span>
                </div>
              </div>
            </div>
            <StepDots step={1} />
            <NavBar
              onBack={() => setStep(0)}
              onNext={() => setStep(2)}
              nextDisabled={!canNextFromHandle}
            />
          </div>
        )}

        {/* Step 3: Theme (niches multi-select) */}
        {step === 2 && (
          <div data-testid="socials-wizard-step-theme">
            <StepHeader
              title="Theme"
              subtitle="Choose your main theme to get hooked up with the right campaigns."
              onClose={() => onOpenChange?.(false)}
            />
            <div className="space-y-3">
              <div className="text-[13px] font-medium text-zinc-300">Tell us what your account is all about!</div>

              {/* Faux Select trigger */}
              <button
                type="button"
                data-testid="socials-wizard-theme-trigger"
                onClick={() => setThemeOpen((v) => !v)}
                className="flex w-full items-center justify-between rounded-xl border border-white/[0.08] bg-black/40 px-3 py-3 text-[13.5px] text-zinc-300 hover:bg-black/60"
              >
                <span>
                  {niches.length === 0
                    ? "Select theme"
                    : niches.join(" · ")}
                </span>
                <ChevronDown size={16} className={`transition-transform ${themeOpen ? "rotate-180" : ""}`} />
              </button>

              {themeOpen && (
                <div className="max-h-56 overflow-y-auto rounded-xl border border-white/[0.08] bg-black/60 p-2">
                  <div className="grid grid-cols-2 gap-1.5">
                    {SOCIAL_NICHES.map((n) => {
                      const selected = niches.includes(n);
                      return (
                        <button
                          key={n}
                          type="button"
                          data-testid={`socials-wizard-theme-${n.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() =>
                            setNiches((prev) => (selected ? prev.filter((x) => x !== n) : [...prev, n]))
                          }
                          className={[
                            "rounded-lg px-3 py-2 text-left text-[12.5px] font-medium transition-colors",
                            selected
                              ? "bg-[#c8ff00]/12 text-[#c8ff00] ring-1 ring-inset ring-[#c8ff00]/30"
                              : "text-zinc-300 hover:bg-white/[0.05]",
                          ].join(" ")}
                        >
                          {n}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {niches.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {niches.map((n) => (
                    <span key={n} className="rounded-full bg-[#c8ff00]/10 px-2.5 py-1 text-[11px] font-semibold text-[#c8ff00]">
                      {n}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {error && (
              <div className="mt-3 rounded-lg border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
                {error}
              </div>
            )}
            <StepDots step={2} />
            <NavBar
              onBack={() => setStep(1)}
              onNext={createSocial}
              nextDisabled={saving}
              nextLoading={saving}
              nextLabel="Continue"
            />
          </div>
        )}

        {/* Step 4: Verify */}
        {step === 3 && (
          <div data-testid="socials-wizard-step-verify">
            <StepHeader
              title={`Verifying @${handle.replace(/^@/, "") || "handle"}`}
              onClose={() => onOpenChange?.(false)}
            />
            <div className="space-y-3">
              <div className="text-[13px] font-semibold text-zinc-200">
                Add the generated code to your profile bio.
              </div>

              <div className="flex items-center justify-between gap-2 rounded-xl border border-white/[0.08] bg-black/40 px-3 py-3">
                <span className="shrink-0">{chosenPlatform?.icon}</span>
                <div
                  data-testid="socials-wizard-verify-code"
                  className="flex-1 truncate font-mono text-[15px] font-bold tracking-wide text-[#c8ff00]"
                >
                  {code || "—"}
                </div>
                <button
                  type="button"
                  data-testid="socials-wizard-verify-copy"
                  onClick={copyCode}
                  className="grid h-8 w-8 place-items-center rounded-md bg-[#c8ff00]/15 text-[#c8ff00] transition-colors hover:bg-[#c8ff00]/25"
                  title="Copy code"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>

              <div className="text-[12px] text-zinc-400">
                Verification is required to apply for campaigns.
              </div>

              <button
                type="button"
                data-testid="socials-wizard-verify-help-toggle"
                onClick={() => setHelpOpen((v) => !v)}
                className="flex items-center gap-1 text-[12.5px] font-semibold text-white hover:text-[#c8ff00]"
              >
                Need Help? Here&apos;s a quick step!
                <ChevronDown size={14} className={`transition-transform ${helpOpen ? "rotate-180" : ""}`} />
              </button>

              {helpOpen && (
                <ol className="ml-4 list-decimal space-y-1 text-[12.5px] text-zinc-300">
                  <li>Open the {platform} app</li>
                  <li>Head to your profile</li>
                  <li>Click Edit profile</li>
                  <li>Paste <span className="font-mono font-bold text-[#c8ff00]">{code}</span> into your Bio.</li>
                  <li>Save by tapping Done.</li>
                </ol>
              )}

              {error && (
                <div className="rounded-lg border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
                  {error}
                </div>
              )}
            </div>
            <StepDots step={3} />
            <NavBar
              onBack={() => setStep(2)}
              onNext={runVerify}
              nextDisabled={!code || verifying}
              nextLoading={verifying}
              nextLabel="Verify"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
